/**
 * Page 1 of Link Artifact Drawer
 * Similar to ../DrawerPageCollectionConfiguration, with the following differences:
 * * No entity selector
 * * User may or may not create a new collection
 *
 * Choose collection OR
 * Configure new collection
 * Name, Description, and Tags
 */
import Markdown from '@wandb/weave/common/components/Markdown';
// @todo: Migrate to 'Dialog.Alert' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Alert} from '@wandb/weave/components/Alert';
import React, {useCallback} from 'react';
import {SingleValue} from 'react-select';

import {capitalizeFirstLetter} from '../../../../../util/string';
import {CollectionInfo} from '../../../../ModelRegistry/utils';
import {Collection} from '../CreateRegisteredCollectionDrawerInner';
import * as S from '../CreateRegisteredCollectionDrawerInner.styles';
import {DrawerPageCollectionConfigurationInner} from '../DrawerPageCollectionConfigurationInner';
import {
  ArtifactCollectionOption,
  getValueForNewType,
  SelectArtifactCollection,
} from '../SelectArtifactCollection';
import {SelectArtifactRegistry} from '../SelectArtifactRegistry';

export type GenericOptionType = {
  label: string;
  value: string;
};

type DrawerPageCollectionConfigurationProps = {
  collection: Collection;
  entityTagsList: string[];
  showModelLimitReached: boolean;
  useExistingCollection: boolean;
  setUseExistingCollection: (val: boolean) => void;
  collectionsListLoading: boolean;
  collectionsList: CollectionInfo[];
  isInvalidCollectionName: boolean;
  setIsInvalidCollectionName: (val: boolean) => void;
  setCollection: (val: Collection) => void;
  selectedRegistry: GenericOptionType | undefined;
  onRegistrySelected: (option: GenericOptionType | undefined) => void;
  registryOptions: GenericOptionType[];
  enableGlobalRegistry: boolean;
  onCreateRegistryClick?: () => void;
};

export const DrawerPageCollectionConfiguration = ({
  collection,
  entityTagsList,
  showModelLimitReached,
  useExistingCollection,
  setUseExistingCollection,
  collectionsListLoading,
  collectionsList,
  isInvalidCollectionName,
  setIsInvalidCollectionName,
  setCollection,
  selectedRegistry,
  onRegistrySelected,
  registryOptions,
  enableGlobalRegistry,
  onCreateRegistryClick,
}: DrawerPageCollectionConfigurationProps) => {
  // Convert to Group<ArtifactCollectionOption[]>
  const portfolioOptions: ArtifactCollectionOption[] = collectionsList
    .filter(c => c.artifactTypeName === collection.artifactTypeName)
    .map(c => ({
      value: c.collectionId,
      name: c.collectionName,
      artifactType: collection.artifactTypeName,
      collectionType: 'portfolio',
      description: c.description,
      // versionIndex is always 1 lower than the number of versions
      numVersions: c.maxVersionIndex == null ? 0 : c.maxVersionIndex + 1,
    }));

  const groupedOptions: Record<string, ArtifactCollectionOption[]> = {};

  portfolioOptions.forEach(option => {
    const {artifactType} = option;
    const group = capitalizeFirstLetter(artifactType ?? '') + 's';
    if (!groupedOptions[group]) {
      groupedOptions[group] = [];
    }

    groupedOptions[group].push(option);
  });

  const transformedPortfolioOptions = Object.entries(groupedOptions).map(
    ([group, options]) => ({
      label: group,
      options,
    })
  );

  const onSelectArtifact = useCallback(
    (option: ArtifactCollectionOption) => {
      setUseExistingCollection(true);

      // max version index in collection
      const selectedCollection = collectionsList.find(
        c => c.collectionId === option.value
      );
      // A collection could be empty, in which case the maxVersion would be -1.
      const maxVersion = selectedCollection?.maxVersionIndex ?? -1;
      setCollection({
        ...collection,
        name: option.name,
        id: option.value,
        description: option?.description ?? '',
        numVersions: maxVersion + 1,
        aliases: selectedCollection?.aliases ?? [],
      });
    },
    [collection, collectionsList, setUseExistingCollection, setCollection]
  );

  return (
    <>
      <S.PageLabelRow>Step 1 of 2 - Collection details</S.PageLabelRow>
      {enableGlobalRegistry && (
        <>
          <S.LabelRow>
            <S.Label>Registry</S.Label>
          </S.LabelRow>
          {registryOptions.length === 0 && !onCreateRegistryClick ? (
            <Alert severity="info">
              There are no registries that allow linking '
              {collection.artifactTypeName}' artifacts. Please head to the
              Registry application to create one.
            </Alert>
          ) : (
            <SelectArtifactRegistry
              value={selectedRegistry}
              options={registryOptions}
              placeholder={'Select registry'}
              onChange={(option: SingleValue<GenericOptionType>) => {
                onRegistrySelected(option ?? undefined);
              }}
              onCreateClick={onCreateRegistryClick}
            />
          )}
        </>
      )}
      <S.LabelRow>
        <S.Label>Collection</S.Label>
      </S.LabelRow>
      <div data-test="collection-selection">
        <SelectArtifactCollection
          loading={collectionsListLoading}
          options={transformedPortfolioOptions ?? []}
          defaultCollectionValue={
            useExistingCollection
              ? collection.name
              : getValueForNewType(collection.artifactTypeName)
          }
          onSelectArtifactCollection={onSelectArtifact}
          includeCreateCollection={true}
          artifactType={collection.artifactTypeName}
          onCreateClick={() => {
            setUseExistingCollection(false);
          }}
          disabled={collection.projectName === ''}
        />
      </div>
      {useExistingCollection ? (
        collection.description !== '' && (
          <>
            <S.LabelRow>
              <S.Label>Description</S.Label>
            </S.LabelRow>
            <S.TextPanel>
              <Markdown content={collection.description} />
            </S.TextPanel>
          </>
        )
      ) : (
        <DrawerPageCollectionConfigurationInner
          collection={collection}
          showModelLimitReached={showModelLimitReached ?? false}
          entityTagsList={entityTagsList}
          isInvalidCollectionName={isInvalidCollectionName}
          setIsInvalidCollectionName={setIsInvalidCollectionName}
          existingCollections={collectionsList}
          setCollection={setCollection}
          defaultArtifactTypeName={collection.artifactTypeName}
          cannotCreateArtifactType={true}
        />
      )}
    </>
  );
};
